























































import Vue from 'vue'
import Component from 'vue-class-component'
import DataTable from '../components/DataTable.vue'
import EmployerCrud from '../components/EmployerCrud.vue'
import { Watch } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'
import {
  Area,
  AreaOrderByInput,
  AreaWhereInput,
  Authority,
  AuthorityWhereInput,
  DataTableAction,
  QueryParams,
  FetchAreasPayload,
  FetchAuthoritiesPayload,
  FetchUsersPayload,
  Filter,
  Role,
  User,
  FilterType,
  FilterField,
  JobListingPlan,
  JobListingPlanConfiguration
} from '@/utils/types'
import { DataTableHeader } from 'vuetify'
import {
  convertToCsv,
  daysAgo,
  daysAgoInISO,
  downloadFile,
  getDaysAgo
} from '@/utils/helpers'
import {
  fetchUsers,
  fetchJobListings,
  updateJobListingExpiry
} from '@/utils/api'

@Component({
  components: { DataTable, EmployerCrud }
})
export default class Employers extends Vue {
  @Action fetchUsers!: (payload: FetchUsersPayload) => Promise<void>
  @Action fetchAreas!: (payload: FetchAreasPayload) => Promise<void>
  @Action updateUser!: (payload: User) => Promise<boolean>
  @Action fetchJobListingPlanConfigurations!: () => Promise<void>
  @Getter jobListingPlanConfigurations!: JobListingPlanConfiguration[]
  @Getter totalUsers!: number
  @Getter users!: User[]
  @Getter areas!: Area[]
  @Getter authorities!: Authority[]
  @Getter jwt!: string
  @Action fetchAuthorities!: (payload: FetchAuthoritiesPayload) => Promise<void>
  headers: DataTableHeader[] = [
    { text: 'אימייל', value: 'email' },
    { text: 'שם פרטי', value: 'firstName' },
    { text: 'שם משפחה', value: 'lastName' },
    { text: 'טלפון', value: 'phone', sortable: false },
    { text: 'תאריך הרשמה', value: 'business.createdAt' },
    { text: 'שם בית העסק', value: 'business.name' },
    { text: 'אזור תעסוקה', value: 'business.area.name' },
    { text: 'רשות', value: 'business.authority.name' },
    { text: 'פרסום ראשון', value: 'business.jobListings[0].createdAt' },
    {
      text: 'פרסום אחרון',
      value: 'business.jobListings[business.jobListings.length - 1]'
    },
    { text: 'פרסומים סה"כ', value: 'business.jobListings.length' }
  ]

  orderBy = AreaOrderByInput.ID_DESC
  dialog = false
  focusedUser: User | null = null
  isLoading = false
  isSnackbarVisible = false
  snackbarMessage = ''
  searchTimeout = 0
  itemsPerPage = 100

  @Watch('currentTab')
  async onCurrentTabChange() {
    await this.fetchPage(1)
  }

  @Watch('$route.query')
  async onQueryChange() {
    await this.fetchPage(1)
  }

  async mounted() {
    await this.fetchData()
    await this.fetchPage(1)
  }

  async fetchPage(page: number, searchText?: string): Promise<void> {
    this.isLoading = true
    await this.fetchUsers({
      limit: this.itemsPerPage,
      page,
      businessArea: this.closestAreaFilter,
      businessAuthority: this.authorityFilter,
      createdAtGte: this.createdAtFilter,
      airedJobListings: this.airedJobListingsFilter,
      isArchived: false,
      isApproved: this.isApproved,
      roles: [Role.EMPLOYER],
      searchText: searchText
    })
    this.isLoading = false
  }

  async fetchData(): Promise<void> {
    if (!this.areas?.length) {
      this.isLoading = true
      await this.fetchAreas({
        isArchived: false,
        orderBy: this.orderBy,
        basic: true
      })
    }
    if (!this.authorities?.length) {
      this.isLoading = true
      await this.fetchAuthorities({
        isArchived: false,
        basic: true
      })
    }
    this.isLoading = false
  }

  async exportEmployers() {
    this.isLoading = true
    let page = 1
    let allEmployers: User[] = []
    let count = 0
    do {
      const employers = await fetchUsers(
        {
          limit: 100,
          isArchived: false,
          roles: [Role.EMPLOYER],
          page: page++
        },
        this.jwt
      )
      count = employers.count
      allEmployers = allEmployers.concat(employers.users)
    } while (allEmployers.length < count)
    const users = allEmployers.map((user) => ({
      ...user,
      mostRecentJobListing: user.mostRecentJobListing
        ? user.mostRecentJobListing.toLocaleDateString('he-IL')
        : '',
      business: {
        ...user.business,
        createdAt: user.business?.createdAt
          ? user.business.createdAt.toLocaleDateString('he-IL')
          : ''
      }
    }))
    downloadFile(
      'employers.csv',
      convertToCsv(
        users,
        this.headers.concat([
          { text: 'מאושר', value: 'business.isApproved' },
          { text: 'תאריך פרסום משרה אחרונה', value: 'mostRecentJobListing' },
          { text: 'האם קיימת משרה פעילה', value: 'hasActiveJobListing' },
          { text: 'מזהה', value: 'id' }
        ])
      )
    )
    this.isLoading = false
  }

  handleClearAllFilters() {
    this.$router.replace({
      path: this.$route.path
    })
  }

  handleStringFilters(filter: Filter<string>) {
    switch (filter.field) {
      case FilterField.areaIds:
        this.closestAreaFilter = {
          idIn: filter.subItems.filter((i) => i.isSelected)?.map((i) => i.value)
        }
        break
      case FilterField.authorityIds:
        this.authorityFilter = {
          idIn: filter.subItems.filter((i) => i.isSelected)?.map((i) => i.value)
        }
        break
      default:
        break
    }
    this.fetchPage(1)
  }

  handleNumberFilters(filter: Filter<number>, value: number) {
    const date = value ? daysAgoInISO(value) : undefined
    switch (filter.field) {
      case FilterField.lastActivity:
        this.lastActivityFilter = date
        break
      case FilterField.createdAt:
        this.createdAtFilter = date
        break
      default:
        break
    }
    this.fetchPage(1)
  }

  async handleSearch(searchText: string) {
    await this.fetchPage(1, searchText)
  }

  async handleSubmit(eventData: User) {
    const wasNotApproved = this.focusedUser?.business?.isApproved !== true
    const isNowApproved = eventData.business?.isApproved === true
    const businessId = this.focusedUser?.business?.id

    const success = await this.updateUser({
      id: eventData.id,
      business: {
        address: eventData.business?.address,
        isApproved: eventData.business?.isApproved
      },
      email: eventData.email,
      firstName: eventData.firstName,
      lastName: eventData.lastName,
      phone: eventData.phone,
      password: eventData.password || undefined
    })
    if (success) {
      if (wasNotApproved && isNowApproved && businessId) {
        await this.resetExpiryForPendingListings(businessId)
      }
      this.snackbarMessage = 'הפעולה בוצעה בהצלחה'
      this.focusedUser = null
      this.isLoading = true
      await this.fetchUsers({
        limit: this.itemsPerPage,
        isArchived: false,
        isApproved: this.isApproved,
        roles: [Role.EMPLOYER]
      })
      this.isLoading = false
    } else {
      this.snackbarMessage = 'הפעולה נכשלה. אנא ודאו את הטופס'
    }
    this.closeDialog()
    this.isSnackbarVisible = true
  }

  async resetExpiryForPendingListings(businessId: string): Promise<void> {
    if (!this.jobListingPlanConfigurations.length) {
      await this.fetchJobListingPlanConfigurations()
    }
    const { jobListings } = await fetchJobListings(
      {
        limit: 100,
        page: 1,
        business: businessId
      },
      this.jwt
    )
    await Promise.all(
      jobListings.map((listing) => {
        const newExpiry = this.computeExpiryByPlan(listing.plan)
        return updateJobListingExpiry(listing.id ?? '', newExpiry, this.jwt)
      })
    )
  }

  computeExpiryByPlan(plan: JobListingPlan): Date {
    const config = this.jobListingPlanConfigurations.find(
      (c) => c.plan === plan
    )
    const days = config?.publishDuration ?? 30
    const date = new Date()
    date.setDate(date.getDate() + days)
    return date
  }

  openDialog(item: User) {
    this.focusedUser = item
    this.dialog = true
  }

  closeDialog() {
    this.dialog = false
    this.focusedUser = null
  }

  get closestAreaFilter(): AreaWhereInput | undefined {
    const query: QueryParams = this.$route.query
    return query.areaIds ? { idIn: query.areaIds?.split(',') } : undefined
  }

  set closestAreaFilter(area: AreaWhereInput | undefined) {
    const query = {
      ...this.$route.query,
      areaIds: area?.idIn?.join(',')
    }
    if (!area?.idIn?.length) {
      delete query.areaIds
    }
    this.$router.replace({
      path: this.$route.path,
      query: query
    })
  }

  get authorityFilter(): AuthorityWhereInput | undefined {
    const query: QueryParams = this.$route.query
    return query.authorityIds
      ? { idIn: query.authorityIds?.split(',') }
      : undefined
  }

  set authorityFilter(authority: AuthorityWhereInput | undefined) {
    const query = {
      ...this.$route.query,
      authorityIds: authority?.idIn?.join(',')
    }
    if (!authority?.idIn?.length) {
      delete query.authorityIds
    }
    this.$router.replace({
      path: this.$route.path,
      query: query
    })
  }

  get createdAtFilter(): string | undefined {
    const query: QueryParams = this.$route.query
    return query.createdAtGte
  }

  set createdAtFilter(date: string | undefined) {
    const query = {
      ...this.$route.query,
      createdAtGte: date
    }
    if (!date) {
      delete query.createdAtGte
    }
    this.$router.replace({
      path: this.$route.path,
      query: query
    })
  }

  get lastActivityFilter(): string | undefined {
    const query: QueryParams = this.$route.query
    return query.lastActivityGte
  }

  set lastActivityFilter(date: string | undefined) {
    const query = {
      ...this.$route.query,
      lastActivityGte: date
    }
    if (!date) {
      delete query.lastActivityGte
    }
    this.$router.replace({
      path: this.$route.path,
      query: query
    })
  }

  get airedJobListingsFilter(): boolean | undefined {
    const query: QueryParams = this.$route.query
    if (query.airedJobListings === 'true') {
      return true
    }
    if (query.airedJobListings === 'false') {
      return false
    }
    return undefined
  }

  set airedJobListingsFilter(value: boolean | undefined) {
    const query = {
      ...this.$route.query
    }
    if (value === undefined) {
      delete query.airedJobListings
    } else {
      query.airedJobListings = String(value)
    }
    this.$router.replace({
      path: this.$route.path,
      query: query
    })
  }

  get currentTab(): number {
    if (this.isApproved) {
      return 1
    } else if (this.isApproved === false) {
      return 2
    }
    return 0
  }

  set currentTab(value: number) {
    if (value === 0) {
      this.$router.replace({
        query: { ...this.$route.query, isApproved: 'null' }
      })
    } else if (value === 1) {
      this.$router.replace({
        query: { ...this.$route.query, isApproved: 'true' }
      })
    } else if (value === 2) {
      this.$router.replace({
        query: { ...this.$route.query, isApproved: 'false' }
      })
    }
  }

  get formattedUsers() {
    return this.users.map((u) => ({
      ...u,
      business: {
        ...u.business,
        createdAt: new Date(u.business?.createdAt || 0).toLocaleDateString(
          'he-IL'
        ),
        jobListings: u.business?.jobListings?.map((jl) => ({
          ...jl,
          createdAt: jl.createdAt
            ? new Date(jl.createdAt).toLocaleDateString('he-IL')
            : ''
        }))
      }
    }))
  }

  get isApproved(): boolean | null {
    const query: QueryParams = this.$route.query
    if (query.isApproved === 'true') {
      return true
    } else if (query.isApproved === 'false') {
      return false
    }
    return null
  }

  set isApproved(value: boolean | null) {
    this.$router.replace({
      query: {
        ...this.$route.query,
        isApproved: String(value)
      }
    })
  }

  get tableActions(): DataTableAction<User>[] {
    return [
      {
        text: 'עריכה',
        icon: 'mdi-pencil',
        handler: this.openDialog.bind(this)
      }
    ]
  }

  get filters(): (
    | Filter<string>
    | Filter<number>
    | Filter<boolean | undefined>
  )[] {
    return [
      {
        action: (filter: Filter<string>) => {
          this.handleStringFilters(filter)
        },
        text: 'אזורי תעסוקה',
        type: FilterType.CHECKBOX,
        field: FilterField.areaIds,
        subItems:
          this.areas?.map((a: Area) => ({
            text: a.name || '',
            value: a.id || '',
            isSelected: !!this.closestAreaFilter?.idIn?.includes(a.id ?? '')
          })) || []
      },
      {
        action: (filter: Filter<string>) => {
          this.handleStringFilters(filter)
        },
        text: 'רשויות',
        type: FilterType.CHECKBOX,
        field: FilterField.authorityIds,
        subItems:
          this.authorities?.map((a: Authority) => ({
            text: a.name || '',
            value: a.id || '',
            isSelected: !!this.authorityFilter?.idIn?.includes(a.id ?? '')
          })) || []
      },
      {
        action: (filter: Filter<number>, value?: number) => {
          this.handleNumberFilters(filter, value || 0)
        },
        text: 'תאריך הרשמה',
        type: FilterType.RADIO,
        field: FilterField.createdAt,
        value: this.createdAtFilter
          ? getDaysAgo(new Date(this.createdAtFilter))
          : 0,
        subItems: [
          {
            text: 'הכל',
            value: 0,
            isSelected: !this.createdAtFilter
          },
          {
            text: 'נרשמו ב-30 יום האחרונים',
            value: 30,
            isSelected: this.createdAtFilter
              ? new Date(this.createdAtFilter) <= daysAgo(30)
              : false
          }
        ]
      },
      {
        action: (filter: Filter<number>, value?: number) => {
          this.handleNumberFilters(filter, value || 0)
        },
        text: 'פעילות אחרונה',
        type: FilterType.RADIO,
        field: FilterField.lastActivity,
        value: this.lastActivityFilter
          ? getDaysAgo(new Date(this.lastActivityFilter))
          : 0,
        subItems: [
          {
            text: 'הכל',
            value: 0,
            isSelected: !this.lastActivityFilter
          },
          {
            text: 'פעילים בשנה האחרונה',
            value: 365,
            isSelected: this.lastActivityFilter
              ? new Date(this.lastActivityFilter) <= daysAgo(365)
              : false
          },
          {
            text: 'פעילים ב-30 יום האחרונים',
            value: 30,
            isSelected: this.lastActivityFilter
              ? new Date(this.lastActivityFilter) <= daysAgo(30)
              : false
          }
        ]
      },
      {
        action: (filter: Filter<boolean | undefined>) => {
          const selectedItem = filter.subItems.find(
            (item) => item.value === filter.value
          )
          this.airedJobListingsFilter = selectedItem
            ? selectedItem.value
            : undefined
        },
        text: 'משרה באוויר',
        type: FilterType.RADIO,
        field: FilterField.airedJobListings,
        value: this.airedJobListingsFilter,
        subItems: [
          {
            text: 'הכל',
            value: undefined,
            isSelected: this.airedJobListingsFilter === undefined
          },
          {
            text: 'כן',
            value: true,
            isSelected: this.airedJobListingsFilter === true
          },
          {
            text: 'לא',
            value: false,
            isSelected: this.airedJobListingsFilter === false
          }
        ]
      }
    ]
  }
}
